import React, { createContext, useEffect, useState, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth.js';
import presenceService from '../services/presenceService.js';

const PresenceContext = createContext();

export const PresenceProvider = ({ children }) => {
  const { user, userProfile } = useAuth();
  const [isInitialized, setIsInitialized] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState(new Set());
  const [presenceData, setPresenceData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Cleanup function defined first to avoid forward reference issues
  const cleanup = useCallback(async () => {
    try {
      await presenceService.destroy();
      setIsInitialized(false);
      setOnlineUsers(new Set());
      setPresenceData({});
      setError(null);
      setIsLoading(false);
    } catch (err) {
      console.error('[PresenceContext] Cleanup error:', err);
    }
  }, []);

  // Initialize presence service when user logs in and is verified
  useEffect(() => {
    const initializePresence = async () => {
      // Only attempt initialization if we have user, userProfile, and user is verified
      if (user?.id && userProfile?.id && userProfile.is_verified && !isInitialized) {
        if (process.env.NODE_ENV === 'development') {
          console.log('[PresenceContext] Initializing presence for verified user:', user.id);
        }
        setIsLoading(true);
        setError(null);
        
        try {
          const result = await presenceService.initialize(user.id);
          if (result.success) {
            if (process.env.NODE_ENV === 'development') {
              console.log('[PresenceContext] Presence initialization successful');
            }
            setIsInitialized(true);
          } else {
            // Handle different failure scenarios
            if (result.needsProfile) {
              if (process.env.NODE_ENV === 'development') {
                console.warn('[PresenceContext] User profile required for presence. Will retry when profile exists.');
              }
              setError('Waiting for user profile creation');
            } else if (result.needsVerification) {
              if (process.env.NODE_ENV === 'development') {
                console.warn('[PresenceContext] User verification required for presence tracking');
              }
              setError('Email verification required for presence tracking');
            } else if (result.authRequired) {
              if (process.env.NODE_ENV === 'development') {
                console.warn('[PresenceContext] Authentication required for presence tracking');
              }
              setError('Authentication required for presence tracking');
            } else {
              if (process.env.NODE_ENV === 'development') {
                console.error('[PresenceContext] Failed to initialize presence:', result.error);
              }
              setError(result.error);
            }
          }
        } catch (err) {
          if (process.env.NODE_ENV === 'development') {
            console.error('[PresenceContext] Presence initialization error:', err);
          }
          setError(err.message);
        } finally {
          setIsLoading(false);
        }
      } else if (user?.id && userProfile?.id && !userProfile.is_verified) {
        // User exists and has profile but is not verified
        if (process.env.NODE_ENV === 'development') {
          console.log('[PresenceContext] User not verified, skipping presence initialization');
        }
        setError('Email verification required for presence tracking');
        setIsLoading(false);
      } else if (user?.id && !userProfile) {
        // User exists but no profile yet - this is expected during auth flow
        if (process.env.NODE_ENV === 'development') {
          console.log('[PresenceContext] User exists but no profile yet, waiting...');
        }
        setError('Waiting for user profile');
        setIsLoading(false);
      } else if (!user?.id) {
        // No user - clean up presence state
        if (isInitialized) {
          if (process.env.NODE_ENV === 'development') {
            console.log('[PresenceContext] User logged out, cleaning up presence');
          }
          cleanup();
        }
      }
    };

    initializePresence();
  }, [user, userProfile, isInitialized, cleanup]);

  // Listen for real-time presence changes
  useEffect(() => {
    const handlePresenceChange = (event) => {
      const { userId, status, presence } = event.detail;
      
      if (process.env.NODE_ENV === 'development') {
        console.log('[PresenceContext] Presence change received:', { userId, status });
      }
      
      // Update presence data
      setPresenceData(prev => ({
        ...prev,
        [userId]: { ...presence, status }
      }));

      // Update online users set
      setOnlineUsers(prev => {
        const newSet = new Set(prev);
        if (status === 'online') {
          newSet.add(userId);
        } else {
          newSet.delete(userId);
        }
        return newSet;
      });
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('presence-changed', handlePresenceChange);
      
      return () => {
        window.removeEventListener('presence-changed', handlePresenceChange);
      };
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (isInitialized) {
        cleanup();
      }
    };
  }, [isInitialized, cleanup]);

  // Get multiple user presence (with caching)
  const getMultipleUserPresence = useCallback(async (userIds) => {
    if (!userIds || userIds.length === 0) {
      return { success: true, presences: {} };
    }

    try {
      const result = await presenceService.getMultipleUserPresence(userIds);
      if (result.success) {
        // Update state with new presence data
        setPresenceData(prev => ({
          ...prev,
          ...result.presences
        }));
        
        // Update online users set
        setOnlineUsers(prev => {
          const newSet = new Set(prev);
          Object.entries(result.presences).forEach(([userId, presence]) => {
            if (presence.status === 'online') {
              newSet.add(userId);
            } else {
              newSet.delete(userId);
            }
          });
          return newSet;
        });
      }
      return result;
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('[PresenceContext] Error getting multiple user presence:', err);
      }
      return { success: false, error: err.message };
    }
  }, []);

  // Get single user presence (with caching)
  const getUserPresence = useCallback(async (userId) => {
    if (!userId) {
      return { success: false, error: 'No user ID provided' };
    }

    try {
      // Handle potential 406 errors by using cached data if available
      const result = await presenceService.getUserPresence(userId);
      if (result.success) {
        // Update state with new presence data
        setPresenceData(prev => ({
          ...prev,
          [userId]: result.presence
        }));
        
        // Update online users set
        setOnlineUsers(prev => {
          const newSet = new Set(prev);
          if (result.presence.status === 'online') {
            newSet.add(userId);
          } else {
            newSet.delete(userId);
          }
          return newSet;
        });
      }
      return result;
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('[PresenceContext] Error getting user presence:', err);
      }
      return { success: false, error: err.message };
    }
  }, []);

  // Set current user's status
  const setStatus = useCallback(async (status) => {
    if (!isInitialized) {
      return { success: false, error: 'Presence service not initialized' };
    }

    try {
      const result = await presenceService.setUserStatus(status);
      if (result.success && user?.id) {
        // Update local state immediately
        setPresenceData(prev => ({
          ...prev,
          [user.id]: {
            ...prev[user.id],
            status,
            last_activity: new Date().toISOString()
          }
        }));

        setOnlineUsers(prev => {
          const newSet = new Set(prev);
          if (status === 'online') {
            newSet.add(user.id);
          } else {
            newSet.delete(user.id);
          }
          return newSet;
        });
      }
      return result;
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('[PresenceContext] Error setting status:', err);
      }
      return { success: false, error: err.message };
    }
  }, [isInitialized, user?.id]);

  // Helper functions
  const isUserOnline = useCallback((userId) => {
    if (!userId) return false;
    return onlineUsers.has(userId);
  }, [onlineUsers]);

  const getUserPresenceData = useCallback((userId) => {
    if (!userId) return { status: 'offline' };
    return presenceData[userId] || { status: 'offline' };
  }, [presenceData]);

  const getOnlineCount = useCallback(() => {
    return onlineUsers.size;
  }, [onlineUsers]);

  const getStats = useCallback(() => {
    return {
      ...presenceService.getCacheStats(),
      contextStats: {
        isInitialized,
        onlineUsersCount: onlineUsers.size,
        cachedPresenceCount: Object.keys(presenceData).length,
        isLoading,
        error
      }
    };
  }, [isInitialized, onlineUsers.size, presenceData, isLoading, error]);

  // Get users by status
  const getUsersByStatus = useCallback((status) => {
    return Object.entries(presenceData)
      .filter(([, presence]) => presence.status === status)
      .map(([userId]) => userId);
  }, [presenceData]);

  const value = {
    // State
    isInitialized,
    isLoading,
    error,
    onlineUsers,
    presenceData,
    
    // Helper methods
    isUserOnline,
    getUserPresence: getUserPresenceData,
    getOnlineCount,
    getUsersByStatus,
    
    // Actions
    getMultipleUserPresence,
    fetchUserPresence: getUserPresence,
    setStatus,
    
    // Utilities
    getStats,
    
    // Constants for status values
    STATUS: {
      ONLINE: 'online',
      AWAY: 'away',
      BUSY: 'busy',
      OFFLINE: 'offline'
    }
  };

  return (
    <PresenceContext.Provider value={value}>
      {children}
    </PresenceContext.Provider>
  );
};

export default PresenceContext;
